import { useEffect, useRef, useState } from "react";
import { Award, CheckCircle, Heart } from "react-feather";
import BigNumber from "bignumber.js";
import { gsap } from "gsap";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { updateNicknamesFromBlockchain } from "@axvdex/state/outbid/outbidThunks";
import { noNicknamesCheck } from "@axvdex/utils/nicknamesSanitization";
import { IActivityMessage } from "@axvdex/utils/interfaces";
import { ReactComponent as IcnTrophy } from "@axvdex/assets/icons/icn-trophy.svg";
import { INicknameSettingsStorageState } from "@axvdex/state/outbid/outbidPersist";
import { useUserSettings } from "@axvdex/hooks/useUserSettings";

export interface IHasActivityMessages {
  activityMessages?: IActivityMessage[];
}

type ActivityMessageHistoryProps = {
  sale: IHasActivityMessages;
};

const ActivityMessageHistoryComponent = ({ sale }: ActivityMessageHistoryProps) => {
  const msgs = sale.activityMessages ?? [];
  const dispatch = useAppDispatch();
  const app = useRef<HTMLDivElement>(null);
  const walletInfo = useAppSelector(selectWalletInfo);
  const [prevState, setPrevState] = useState({ sale });
  const assets = useAppSelector(state => state.outbid.outbidAssets);
  const nicknames = useAppSelector(state => state.outbid.nicknames);
  const { userSettings } = useUserSettings();

  useEffect(() => {
    setPrevState({ sale });
  }, [sale]);

  useEffect(() => {
    if (walletInfo.isConnected) {
      const msgsWithoutNicknames = [];
      for (const msg of msgs) {
        if (!nicknames[msg.address]) {
          msgsWithoutNicknames.push(msg.address);
        }
      }
      if (0 !== msgsWithoutNicknames.length) {
        dispatch(
          updateNicknamesFromBlockchain({
            addresses: msgsWithoutNicknames,
          })
        );
      }
    }
  }, [msgs, walletInfo.isConnected, userSettings]);

  useEffect(() => {
    if (
      sale.activityMessages &&
      0 < sale.activityMessages.length &&
      JSON.stringify(sale.activityMessages[0]) !== JSON.stringify(prevState.sale.activityMessages[0])
    ) {
      const ctx = gsap.context(() => {
        gsap.from(`.newActivity`, 1, {
          ease: "Power3.easeInOut",
          duration: 0.5,
          y: "50%",
          opacity: 0,
        });
      }, app);
      return () => ctx.revert();
    }
  }, [sale]);

  const parseIncentive = incentive => {
    if (incentive.asset) {
      const asset = assets[incentive.asset.info.token?.contract_addr || incentive.asset.info.native_token?.denom];
      if (!asset) return null;
      // return (
      //   <div className="inlineFlexbox">
      //     {BigNumber(incentive.asset.amount).div(Math.pow(10, asset.decimals)).toString(10)}
      //     <span className="visualiserListItemTokenImage">
      //       <img src={imgSanitize(asset.symbol)} alt={asset.symbol} />
      //     </span>
      //   </div>
      // );
      return `${BigNumber(incentive.asset.amount).div(Math.pow(10, asset.decimals)).toString(10)} ${asset.symbol}`;
    }
    if (incentive.nft) {
      return `NFT`;
    }
  };

  if (0 === msgs.length) return null;
  return (
    <div
      ref={app}
      style={{
        marginTop: "1em",
        maxHeight: "15em",
        overflowY: "scroll",
      }}
      className={"custom-scrollbar"}
    >
      {msgs.map((msg, i) => {
        const nickname = `${nicknames[msg.address]?.nickname ? noNicknamesCheck(nicknames[msg.address]?.nickname, userSettings) : "------"
          }`;

        return (
          <div
            key={msg.timestamp + "_" + i}
            className={`${"topBidder" === msg.type && 0 === i
                ? "topBidderActivityRow"
                : "lastBidder" === msg.type && 0 === i
                  ? "bonusActivityRow"
                  : ""
              }`}
            style={{
              alignItems: "center",
              display: "flex",
              backgroundColor: "incentive" === msg.type ? "#8B8000" : "rgba(255,255,255,0.1)",
              borderRadius: "1em",
              padding: ".75em",
              marginTop: ".5em",
              marginRight: ".5em",
            }}
          >
            {"topBidder" === msg.type ? (
              <Award style={{ minWidth: "1em", width: "1.5em", marginRight: "1em", marginLeft: "0.25em" }} />
            ) : "lastBidder" === msg.type ? (
              <Heart style={{ minWidth: "1em", width: "1.5em", marginRight: "1em", marginLeft: "0.25em" }} />
            ) : "soldOut" === msg.type ? (
              <CheckCircle style={{ minWidth: "1em", width: "1.5em", marginRight: "1em", marginLeft: "0.25em" }} />
            ) : "incentive" === msg.type ? (
              <IcnTrophy style={{ minWidth: "1em", width: "1.75em", marginRight: "1em", marginLeft: "0.125em" }} />
            ) : null}

            <span style={{ fontSize: ".8em" }}>
              {"topBidder" === msg.type ? (
                `${nickname} ${msg.is_same_position ? "reinforced" : "took"} ${0 === msg.index ? "1st" : 1 === msg.index ? "2nd" : "3rd"
                } place with ${BigNumber(msg.newAmount).div(Math.pow(10, 6)).decimalPlaces(4).toString(10)} tickets`
              ) : "lastBidder" === msg.type ? (
                `${nickname} took the bonus ${BigNumber(msg.newAmount)
                  .div(Math.pow(10, 6))
                  .decimalPlaces(4)
                  .toString(10)} 💰`
              ) : "soldOut" === msg.type ? (
                `${nickname} completed the sale`
              ) : "incentive" === msg.type ? (
                <div>
                  {`${nickname} got a reward: `} {parseIncentive(msg.incentive)}
                </div>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default ActivityMessageHistoryComponent;
